import { Popover } from 'antd';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AvatarBox, { AvatarType } from '~src/shared/avatarBox/AvatarBox';

import SvgIcon, { SvgIconName } from '~src/shared/SvgIcon';

import * as SC from './listFilters.sc';
import { FilterContent } from './listFilters.sc';

export interface FilterI {
  id: string;
  labelKey: string;
  iconName?: SvgIconName;
  tooltipKey?: string;
  itemsAmount?: number;
  disabled?: boolean;
  imageUrl?: string;
  userName?: string;
  tilt?: string;
  viewBoxWidth?: number;
}

interface DefaultProps {
  titleKey: string;
}

interface Props extends Partial<DefaultProps> {
  children?: ReactNode;
  hideLabel?: boolean;
  filterOptions: FilterI[];
  initialFilter?: string | null;
  subjectName?: string;

  doFilter: (i: number) => void;
}

const FilterBtn: FC<{
  filter: FilterI;
  i: number;
  activeFilter: string;
  setActiveFilter(id: string): void;
  doFilter?(i: number): void;
  viewBoxWidth?: string;
}> = ({ filter, i, activeFilter, setActiveFilter, doFilter }) => {
  const { t } = useTranslation();

  const onFilterClick = () => {
    setActiveFilter(filter.id);
    if (doFilter) {
      doFilter(i);
    }
  };
  return (
    <SC.FilterBtn
      disabled={filter.disabled}
      className={activeFilter === filter.id ? 'active' : ''}
      onClick={onFilterClick}
    >
      {filter.iconName && (
        <SC.FilterIcon $tilt={filter.tilt}>
          <SvgIcon name={filter.iconName} viewBoxWidth={filter.viewBoxWidth} />
        </SC.FilterIcon>
      )}
      {(filter.imageUrl || filter.userName) && (
        <SC.FilterIcon>
          <AvatarBox type={AvatarType.User} size={25} url={filter.imageUrl} name={filter.userName} />
        </SC.FilterIcon>
      )}
      <SC.FilterName data-translation={filter.labelKey}>
        <div>{t(filter.labelKey)}</div>
        <SC.FilterAmount $itemAmount={Boolean(filter.itemsAmount)}>
          {' '}
          {filter.itemsAmount && `(${filter.itemsAmount})`}
        </SC.FilterAmount>
      </SC.FilterName>
    </SC.FilterBtn>
  );
};

const ListFilters: FC<Props> = React.memo(function RenderListFilters(props) {
  const {
    filterOptions,
    initialFilter,
    subjectName,
    doFilter,
    hideLabel,
    titleKey = 'x.scales.list.filters.filters_title',
    children,
  } = props;
  const [activeFilter, setActiveFilter] = useState(initialFilter || filterOptions[0].id);
  const { t } = useTranslation();

  useEffect(() => {
    setActiveFilter(initialFilter || filterOptions[0].id);
  }, [initialFilter]);

  return (
    <SC.FiltersContainer>
      {!hideLabel && (
        <SC.FilterBox>
          <SC.LabelAbout data-translation={titleKey}>{t(titleKey)}:</SC.LabelAbout>
        </SC.FilterBox>
      )}
      {filterOptions.map((filter, i) => {
        const Btn = (
          <FilterBtn
            filter={filter}
            i={i}
            key={i}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            doFilter={doFilter}
          />
        );
        return (
          <SC.FilterBox $hideLabel={hideLabel} key={filter.id}>
            {filter.tooltipKey ? (
              <Popover
                content={<div style={{ maxWidth: 200 }}>{t(filter.tooltipKey, { subjectName })}</div>}
                trigger="hover"
                placement="bottom"
              >
                <FilterContent>{Btn}</FilterContent>
              </Popover>
            ) : (
              <FilterContent>{Btn}</FilterContent>
            )}
          </SC.FilterBox>
        );
      })}
      {children}
    </SC.FiltersContainer>
  );
});

export default ListFilters;
